/**
 * Where the counter items go, relative to the shop's.
 *
 * Two searches answer one question. `/api/foods/search` reads the catalogue and
 * `/api/fastfood/items` reads eight chains' menus, and until now the menu always
 * rendered first — not because it answered better, but because it happened to be
 * the other array. Typing "pomidorai" on the Today screen produced a wall of
 * croissants with tomatoes in them, and the plain tomato underneath: exactly the
 * complaint the catalogue ranking had just been rebuilt to fix, arriving through
 * a door the rebuild never touched.
 *
 * So both lists are put on one scale — the same `scoreFood` the server ranked
 * each of them with — and the better answer goes first. Counter rows far below
 * the best thing found anywhere are dropped rather than merely demoted: six
 * croissants under the tomato is still six croissants.
 *
 * Deliberately scored without the diary. Which section wins should be a question
 * about the words that were typed; a counter item has no usage to be counted
 * against, so letting familiarity into this comparison would tilt it toward the
 * shop every time. The order *within* the shop list still uses it.
 */

import type { FastfoodItem } from '@gramas/shared'
import { prepareQuery, scoreFood } from '@gramas/shared'
import type { LocalFood } from '../../db/dexie.js'

/**
 * How far below the best answer found anywhere a counter row may still be shown.
 *
 * Relative rather than absolute, for the same reason COVERAGE_BAND is: where the
 * catalogue holds the thing itself, a menu item merely containing the word is
 * noise; where it holds nothing, the same menu item is the only useful row on
 * the screen and must survive. At 0.85 a query the shop answers exactly clears
 * the menu, and a query only the menu answers keeps all of it.
 */
const COUNTER_BAND = 0.85

export interface CounterPlacement {
  items: FastfoodItem[]
  /** Render the counter section above the shop's rather than below it. */
  first: boolean
}

const NONE: CounterPlacement = { items: [], first: false }

export function placeCounterItems(
  query: string,
  counter: readonly FastfoodItem[],
  shop: readonly LocalFood[],
): CounterPlacement {
  const prepared = prepareQuery(query)
  if (prepared.terms.length === 0 || counter.length === 0) return NONE

  // The chain stands in for the brand, which is what makes "circle k latte" find
  // the right chain's — the same two fields the server ranked these by.
  const scored = counter
    .map((item) => ({
      item,
      score: scoreFood(prepared, { nameLt: item.name, brand: item.chainSlug }).score,
    }))
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score)

  const bestCounter = scored[0]?.score ?? 0
  const bestShop = shop.reduce((best, food) => Math.max(best, scoreFood(prepared, food).score), 0)
  const floor = Math.max(bestCounter, bestShop) * COUNTER_BAND

  return {
    items: scored.filter((row) => row.score >= floor).map((row) => row.item),
    first: bestCounter > bestShop,
  }
}
